import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight } from "lucide-react";
import StepDetailBody from "./StepDetailBody";

/**
 * A single recovery-plan step that expands into an AI-built execution guide
 * (step-by-step how-to, documents to build, time estimate, saved checklist).
 *
 * Props:
 *  - stepText: the step/action string
 *  - testid: base data-testid
 *  - loadDetail: async () => { detail, checklist }
 *  - saveItem: async (itemIndex, done) => { checklist }
 */
export default function RecoveryStep({ stepText, testid = "recovery-step", loadDetail, saveItem }) {
  const [open, setOpen] = useState(false);
  const [everOpened, setEverOpened] = useState(false);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) setEverOpened(true);
  };

  return (
    <li className="rounded-xl border border-[#1e3d3a]/10 bg-white overflow-hidden">
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        onClick={toggle}
        className="w-full flex items-start gap-2 px-3 py-2.5 text-left hover:bg-[#c9a961]/8 transition-colors"
      >
        <ChevronRight className={`h-4 w-4 text-[#c9a961] shrink-0 mt-0.5 transition-transform ${open ? "rotate-90" : ""}`} />
        <span className="text-sm text-[#1e3d3a]/85 flex-1">{stepText}</span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            data-testid={`${testid}-panel`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 border-t border-[#1e3d3a]/8">
              <StepDetailBody active={everOpened} loadDetail={loadDetail} saveItem={saveItem} testid={testid} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}
